import type { ShipNodePrinter, ShipNodeStation } from './types';

export type StatusLight = {
  isOnline: boolean;
  label: 'Online' | 'Offline' | 'Turned off';
  /** Muted text beside the light: when the station was last seen, or why the printer is offline. */
  detail?: string;
};

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** "5 minutes ago", counted down (1 min 52 s is "1 minute ago"), for a past time. */
export function formatTimeAgo(iso: string, now: Date = new Date()): string {
  const seconds = Math.max(
    0,
    Math.floor((now.getTime() - new Date(iso).getTime()) / 1000),
  );
  if (seconds < 60) return 'less than a minute ago';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return relative.format(-minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return relative.format(-hours, 'hour');
  return relative.format(-Math.floor(hours / 24), 'day');
}

const dateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** "Sep 29, 2026", in the viewer's time zone. */
export function formatDate(iso: string): string {
  return dateFormat.format(new Date(iso));
}

/* The backend derives `online` (checked in within 90 s); the web only reads it. */
export function stationStatus(
  station: Pick<ShipNodeStation, 'online' | 'lastSeenAt'>,
  now: Date = new Date(),
): StatusLight {
  if (station.online) {
    return { isOnline: true, label: 'Online' };
  }
  return {
    isOnline: false,
    label: 'Offline',
    detail: station.lastSeenAt
      ? `last seen ${formatTimeAgo(station.lastSeenAt, now)}`
      : 'never seen',
  };
}

export function printerStatus(
  printer: Pick<ShipNodePrinter, 'online' | 'enabled' | 'reason'>,
): StatusLight {
  if (printer.online) {
    return { isOnline: true, label: 'Online' };
  }
  if (!printer.enabled) {
    return { isOnline: false, label: 'Turned off' };
  }
  return {
    isOnline: false,
    label: 'Offline',
    detail: printer.reason || undefined,
  };
}

/** The message from a rejected `BaseService` call (`{ code, error, status }`), or `fallback`. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { error?: unknown } | null)?.error;
  return typeof message === 'string' && message ? message : fallback;
}

/** "9:41" for a countdown; never negative. */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}
